import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Cluster,
  FoldPanel,
  Input,
  RowList,
  Stack,
  TokenRow,
  Typography,
} from '@src/components';
import { readCssVariables } from '@src/pages/UiKitPage/readCssVariables';
import { groupTokens } from '@src/pages/UiKitPage/tokenGroups';
import { useTheme } from '@src/theme/useTheme';

/**
 * Every token in the system, with the value it resolves to in the theme that is
 * currently on. Nothing here is hardcoded — see readCssVariables.
 */
export function TokenSection() {
  const { theme, seasonPackage } = useTheme();
  const [query, setQuery] = useState('');

  // Package styles enter the DOM at commit; read their resolved values afterwards.
  const [variables, setVariables] = useState<ReturnType<typeof readCssVariables>['variables']>([]);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setVariables(readCssVariables(theme).variables));
    return () => cancelAnimationFrame(frame);
  }, [theme, seasonPackage]);

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = needle
      ? variables.filter(
          (variable) =>
            variable.name.toLowerCase().includes(needle) ||
            variable.value.toLowerCase().includes(needle),
        )
      : variables;
    return groupTokens(matching);
  }, [variables, query]);

  return (
    <FoldPanel
      id="tokens"
      title="Design tokens"
      hint={`Read live out of styles/tokens.css and the ${seasonPackage.id} package — ${variables.length} tokens, resolved for the ${theme} theme.`}
      example={`color: var(--color-accent);
background-color: var(--color-surface);
border-color: var(--color-selection);`}
    >
      <Input
        label="Filter"
        type="search"
        value={query}
        onValueChange={setQuery}
        placeholder="space, accent, shadow…"
      />

      {groups.length === 0 && (
        <Typography as="p" role="body/sm">
          No token matches “{query}”.
        </Typography>
      )}

      {groups.map((group) => (
        <Stack gap={2} key={group.id}>
          <Cluster align="baseline" gap={2}>
            <Typography as="h3" role="title/sub">
              {group.label}
            </Typography>
            <Badge>{group.variables.length}</Badge>
            <Typography as="p" role="meta/helper">
              {group.hint}
            </Typography>
          </Cluster>
          <RowList>
            {group.variables.map((variable) => (
              <TokenRow
                key={variable.name}
                name={variable.name}
                value={variable.value}
                preview={group.preview}
              />
            ))}
          </RowList>
        </Stack>
      ))}
    </FoldPanel>
  );
}
